import { useState, type ReactNode } from 'react';
import type { DashboardInsight, DashboardLink, DashboardResponse } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { formatMoney } from './money';
import { useCountUp } from './countUp';
import { Icon, type IconName } from './icons';
import {
  AgingDonut,
  CashTrendChart,
  MarginTrendChart,
  RankedList,
  RevenueExpenseChart,
  Sparkline,
  TradeChart,
} from './DashboardCharts';
import { delta, insightText, pointsDelta, SEVERITY_LABEL, type Delta } from './dashboardText';
import type { CenterFilters } from './centerReports';

/** وجهات اللوحة — كل بطاقة أو تنبيه يفتح الشاشة التى يأتى منها رقمه. */
export type DashboardTarget =
  | { kind: 'treasuryAccounts' }
  | { kind: 'reports'; tab: 'income' | 'balance' | 'aging' | 'apAging' | 'vat' }
  | { kind: 'reportsCenter'; initial?: Partial<CenterFilters> }
  | { kind: 'reconciliation'; bankAccountId?: string }
  | { kind: 'periods' }
  | { kind: 'settings'; tab: 'einvoice' }
  | { kind: 'customers'; customerId?: string }
  | { kind: 'suppliers'; supplierId?: string }
  | { kind: 'ledger'; accountCode: string | null }
  | { kind: 'inventory' }
  | { kind: 'invoices' }
  | { kind: 'expenses' }
  | { kind: 'journal' };

export function targetOf(link: DashboardLink): DashboardTarget {
  switch (link.screen) {
    case 'report':
      return { kind: 'reports', tab: link.tab };
    case 'customer':
      return { kind: 'customers', customerId: link.customerId };
    case 'supplier':
      return { kind: 'suppliers', supplierId: link.supplierId };
    case 'ledger':
      return { kind: 'ledger', accountCode: link.accountCode };
    case 'inventory':
      return { kind: 'inventory' };
    case 'treasuryAccounts':
      return { kind: 'treasuryAccounts' };
    case 'reconciliation':
      return { kind: 'reconciliation' };
    case 'reportsCenter': {
      if (!link.report) return { kind: 'reportsCenter' };
      const initial: Partial<CenterFilters> = { report: link.report };
      if (link.from) initial.from = link.from;
      if (link.to) initial.to = link.to;
      if (link.days !== undefined) initial.days = String(link.days);
      if (link.groupBy) initial.salesGroupBy = link.groupBy;
      return { kind: 'reportsCenter', initial };
    }
    case 'periods':
      return { kind: 'periods' };
  }
}

const SEVERITY_ICON: Record<DashboardInsight['severity'], IconName> = {
  critical: 'alert',
  warning: 'alert',
  info: 'info',
  good: 'check',
};

const SEVERITY_ORDER: readonly DashboardInsight['severity'][] = [
  'critical',
  'warning',
  'info',
  'good',
];

function DeltaPill({
  change,
  upIsGood,
  unit = '٪',
}: {
  change: Delta;
  upIsGood: boolean;
  unit?: string;
}) {
  if (change.direction === 'flat') return <span className="dash-delta flat">بلا تغيير</span>;
  const good = (change.direction === 'up') === upIsGood;
  return (
    <span className={'dash-delta ' + (good ? 'good' : 'bad')}>
      <Icon name={change.direction === 'up' ? 'arrowUp' : 'arrowDown'} size={13} />
      {change.percent === null ? 'جديد' : change.percent + unit}
    </span>
  );
}

function KpiCard({
  label,
  icon,
  amount,
  currency,
  change,
  upIsGood = true,
  unit,
  sparkline,
  sub,
  onOpen,
}: {
  label: string;
  icon: IconName;
  /** المبلغ الخام — يُعدّ من صفر إليه ثم يُنسَّق. */
  amount: string;
  currency?: string;
  change: Delta;
  upIsGood?: boolean;
  unit?: string;
  sparkline?: readonly (string | null)[];
  sub?: ReactNode;
  onOpen: () => void;
}) {
  const shown = useCountUp(amount);
  const good = change.direction === 'flat' || (change.direction === 'up') === upIsGood;
  return (
    <button type="button" className={'dash-kpi ' + (good ? 'is-good' : 'is-bad')} onClick={onOpen}>
      <span className="dash-kpi-head">
        <span className="dash-kpi-icon" aria-hidden="true">
          <Icon name={icon} size={18} />
        </span>
        <span className="dash-kpi-label">{label}</span>
      </span>
      <span className="dash-kpi-value">
        {/* القارئ يسمع الرقم النهائى لا أرقام العدّ الوسيطة */}
        <bdi aria-hidden="true">{formatMoney(shown)}</bdi>
        <span className="sr-only">{formatMoney(amount)}</span>
        {currency && <small> {currency}</small>}
      </span>
      <span className="dash-kpi-foot">
        <DeltaPill change={change} upIsGood={upIsGood} unit={unit} />
        {sparkline && sparkline.length > 0 && (
          <Sparkline values={sparkline} tone={good ? 'good' : 'bad'} label={'اتجاه ' + label} />
        )}
      </span>
      {sub && <span className="dash-kpi-sub">{sub}</span>}
    </button>
  );
}

function InsightItem({
  insight,
  currency,
  index,
  onOpen,
}: {
  insight: DashboardInsight;
  currency: string;
  index: number;
  onOpen: () => void;
}) {
  const text = insightText(insight, currency);
  return (
    <li
      className={'dash-insight sev-' + insight.severity}
      style={{ animationDelay: index * 60 + 'ms' }}
    >
      <span className="dash-sev" aria-hidden="true">
        <Icon name={SEVERITY_ICON[insight.severity]} size={16} />
      </span>
      <div className="dash-insight-body">
        <span className="dash-sev-label">{SEVERITY_LABEL[insight.severity]}</span>
        <p className="dash-insight-title">{text.title}</p>
        {text.detail && <p className="dash-insight-detail">{text.detail}</p>}
      </div>
      <button type="button" className="dash-insight-open" onClick={onOpen}>
        التفاصيل
        <Icon name="chevron" size={14} />
      </button>
    </li>
  );
}

export function Panel({
  title,
  icon,
  action,
  children,
  className,
}: {
  title: string;
  icon?: IconName;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={'dash-panel' + (className ? ' ' + className : '')}>
      <header className="dash-panel-head">
        <h2>
          {icon && <Icon name={icon} size={18} className="dash-panel-icon" />}
          {title}
        </h2>
        {action}
      </header>
      {children}
    </section>
  );
}

interface Props {
  data: DashboardResponse;
  currency: string;
  inventoryEnabled: boolean;
  loading?: boolean;
  onNavigate: (target: DashboardTarget) => void;
  /** زر الحدود ولوحتها — لمن يدير الإعدادات وحده. */
  insightsAction?: ReactNode;
  insightsTop?: ReactNode;
  /** بطاقة حالة الفاتورة الإلكترونية (تقرأ الخادم بنفسها). */
  eInvoiceCard?: ReactNode;
}

/**
 * جسم اللوحة: يعرض ولا يجلب. منفصل عن `Dashboard` حتى يُعرض فى معرض التصميم ببيانات
 * ثابتة، ولا يُحسب فيه رصيد — كل رقم من الخادم، والواجهة تصوغ وتفتح التقرير.
 */
export default function DashboardBody({
  data,
  currency,
  inventoryEnabled,
  loading = false,
  onNavigate,
  insightsAction,
  insightsTop,
  eInvoiceCard,
}: Props) {
  const [allKpis, setAllKpis] = useState(false);

  type Todo = { count: number; label: string; icon: IconName; target: DashboardTarget };
  const todoItems = (
    [
      {
        count: data.todos.overdueInvoices,
        label: 'فواتير عملاء متأخرة',
        icon: 'invoice',
        target: { kind: 'reports', tab: 'aging' },
      },
      {
        count: data.todos.billsDueSoon,
        label: 'فواتير موردين للسداد قريبًا',
        icon: 'bill',
        target: { kind: 'reports', tab: 'apAging' },
      },
      {
        count: data.unreconciledBankLines,
        label: 'سطور بنك تحتاج مطابقة',
        icon: 'reconcile',
        target: { kind: 'reconciliation' },
      },
      {
        count: data.todos.periodsToClose,
        label: 'فترات منتهية لم تُقفل',
        icon: 'calendar',
        target: { kind: 'periods' },
      },
      {
        count: data.todos.draftJournals,
        label: 'قيود غير منشورة',
        icon: 'journal',
        target: { kind: 'journal' },
      },
    ] satisfies Todo[]
  ).filter((item) => item.count > 0);

  const severityCounts = SEVERITY_ORDER.map((severity) => ({
    severity,
    count: data.insights.filter((insight) => insight.severity === severity).length,
  })).filter((item) => item.count > 0);
  const showInventory = inventoryEnabled || !Money.of(data.kpis.inventory.value).isZero();

  return (
    <div className={'dash-body' + (loading ? ' is-loading' : '')}>
      <section className="dash-todos" aria-label="مهام اليوم">
        <h2>
          <Icon name="sparkle" size={18} />
          مهام اليوم
        </h2>
        {todoItems.length === 0 ? (
          <p className="dash-all-clear">
            <Icon name="check" size={16} /> لا شىء متأخر ينتظرك اليوم
          </p>
        ) : (
          <ul>
            {todoItems.map((item) => (
              <li key={item.label}>
                <button type="button" onClick={() => onNavigate(item.target)}>
                  <Icon name={item.icon} size={16} />
                  <b>{item.count}</b> {item.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className={'dash-kpis' + (allKpis ? ' show-all' : '')}>
        <KpiCard
          label="الإيراد"
          icon="chart"
          amount={data.kpis.revenue.value}
          currency={currency}
          change={delta(data.kpis.revenue.value, data.kpis.revenue.previous)}
          sparkline={data.kpis.revenue.sparkline}
          onOpen={() => onNavigate({ kind: 'reports', tab: 'income' })}
        />
        <KpiCard
          label="صافى الربح"
          icon="sparkle"
          amount={data.kpis.netProfit.value}
          currency={currency}
          change={delta(data.kpis.netProfit.value, data.kpis.netProfit.previous)}
          sparkline={data.kpis.netProfit.sparkline}
          onOpen={() => onNavigate({ kind: 'reports', tab: 'income' })}
        />
        <KpiCard
          label="النقدية والبنوك"
          icon="wallet"
          amount={data.kpis.cash.value}
          currency={currency}
          change={delta(data.kpis.cash.value, data.kpis.cash.previous)}
          sparkline={data.kpis.cash.sparkline}
          onOpen={() => onNavigate({ kind: 'treasuryAccounts' })}
        />
        <KpiCard
          label="مستحق من العملاء"
          icon="users"
          amount={data.kpis.receivables.value}
          currency={currency}
          change={delta(data.kpis.receivables.value, data.kpis.receivables.previous)}
          sparkline={data.kpis.receivables.sparkline}
          upIsGood={false}
          onOpen={() => onNavigate({ kind: 'reports', tab: 'aging' })}
        />
        <KpiCard
          label="مجمل الربح"
          icon="tag"
          amount={data.kpis.grossProfit.value}
          currency={currency}
          change={delta(data.kpis.grossProfit.value, data.kpis.grossProfit.previous)}
          sparkline={data.kpis.grossProfit.sparkline}
          sub={
            <>
              الهامش{' '}
              {data.kpis.grossMargin.value === null ? (
                '—'
              ) : (
                <bdi>{data.kpis.grossMargin.value + '٪'}</bdi>
              )}{' '}
              {data.kpis.grossMargin.previous !== null && (
                <DeltaPill
                  change={pointsDelta(data.kpis.grossMargin.value, data.kpis.grossMargin.previous)}
                  upIsGood
                  unit=" نقطة"
                />
              )}
            </>
          }
          onOpen={() => onNavigate({ kind: 'reports', tab: 'income' })}
        />
        <KpiCard
          label="متأخر على العملاء"
          icon="alert"
          amount={data.kpis.overdueReceivables.value}
          currency={currency}
          change={delta(data.kpis.overdueReceivables.value, data.kpis.overdueReceivables.previous)}
          upIsGood={false}
          onOpen={() => onNavigate({ kind: 'reports', tab: 'aging' })}
        />
        <KpiCard
          label="مستحق للموردين"
          icon="truck"
          amount={data.kpis.payables.value}
          currency={currency}
          change={delta(data.kpis.payables.value, data.kpis.payables.previous)}
          sparkline={data.kpis.payables.sparkline}
          upIsGood={false}
          sub={'متأخر ' + formatMoney(data.payables.overdue)}
          onOpen={() => onNavigate({ kind: 'reports', tab: 'apAging' })}
        />
        {showInventory && (
          <KpiCard
            label="قيمة المخزون"
            icon="box"
            amount={data.kpis.inventory.value}
            currency={currency}
            change={delta(data.kpis.inventory.value, data.kpis.inventory.previous)}
            sparkline={data.kpis.inventory.sparkline}
            onOpen={() =>
              onNavigate(
                inventoryEnabled ? { kind: 'inventory' } : { kind: 'reports', tab: 'balance' },
              )
            }
          />
        )}
      </div>
      {/* على الموبايل أربعة مؤشرات أولاً والبقية بطلب — الشاشة الأولى للأهم */}
      <button
        type="button"
        className="ghost dash-more-kpis"
        aria-expanded={allKpis}
        onClick={() => setAllKpis((open) => !open)}
      >
        {allKpis ? 'مؤشرات أقل' : 'كل المؤشرات'}
      </button>

      <div className="dash-row">
        <Panel
          title="تنبيهات ذكية"
          icon="sparkle"
          className="dash-insights-panel"
          action={insightsAction}
        >
          {insightsTop}
          {severityCounts.length > 0 && (
            <p className="dash-sev-summary">
              {severityCounts.map((item) => (
                <span key={item.severity} className={'dash-sev-chip sev-' + item.severity}>
                  <b>{item.count}</b> {SEVERITY_LABEL[item.severity]}
                </span>
              ))}
            </p>
          )}
          {data.insights.length === 0 ? (
            <p className="dash-empty-line">لا شىء يستحق التنبيه فى هذه الفترة.</p>
          ) : (
            <ul className="dash-insights">
              {data.insights.map((insight, index) => (
                <InsightItem
                  key={insight.rule + index}
                  insight={insight}
                  currency={currency}
                  index={index}
                  onOpen={() => onNavigate(targetOf(insight.link))}
                />
              ))}
            </ul>
          )}
          <p className="dash-footnote">
            تُحسب على الخادم بقواعد من دفاترك المنشورة — لا تغادر بياناتك البرنامج.
          </p>
        </Panel>
        <Panel
          title="أعمار ديون العملاء"
          icon="users"
          action={
            <button
              type="button"
              className="ghost"
              onClick={() => onNavigate({ kind: 'reports', tab: 'aging' })}
            >
              التقرير
            </button>
          }
        >
          <AgingDonut
            aging={data.receivablesAging}
            total={data.receivables.total}
            currency={currency}
            onOpen={() => onNavigate({ kind: 'reports', tab: 'aging' })}
          />
        </Panel>
      </div>

      <div className="dash-row wide-start">
        <Panel
          title="المبيعات مقابل المشتريات — آخر ١٢ شهرًا"
          icon="invoice"
          action={
            <button
              type="button"
              className="ghost"
              onClick={() =>
                onNavigate(
                  targetOf({
                    screen: 'reportsCenter',
                    report: 'sales',
                    from: data.monthly[0]?.from,
                    to: data.monthly[data.monthly.length - 1]?.to,
                  }),
                )
              }
            >
              تحليل المبيعات
            </button>
          }
        >
          <TradeChart data={data.trade} currency={currency} />
        </Panel>
        <Panel title="هامش مجمل الربح" icon="tag">
          <MarginTrendChart
            months={data.monthly.map((month) => month.month)}
            margins={data.kpis.grossMargin.sparkline}
          />
        </Panel>
      </div>

      <div className="dash-row wide-start">
        <Panel title="الإيراد مقابل المصروف — آخر ١٢ شهرًا" icon="chart">
          <RevenueExpenseChart data={data.monthly} currency={currency} />
        </Panel>
        <Panel title="اتجاه النقدية والبنوك" icon="wallet">
          <CashTrendChart data={data.cashTrend} currency={currency} />
        </Panel>
      </div>

      <div className="dash-row thirds">
        <Panel
          title="أكبر العملاء"
          icon="users"
          action={
            <button
              type="button"
              className="ghost"
              onClick={() =>
                onNavigate(
                  targetOf({
                    screen: 'reportsCenter',
                    report: 'sales',
                    from: data.period.from,
                    to: data.period.to,
                    groupBy: 'customer',
                  }),
                )
              }
            >
              تحليل المبيعات
            </button>
          }
        >
          <RankedList
            rows={data.top.customers}
            empty="لا مبيعات فى الفترة."
            onOpen={(row) => onNavigate(targetOf(row.link))}
          />
        </Panel>
        <Panel title="أكثر الأصناف مبيعًا" icon="tag">
          <RankedList
            rows={data.top.items}
            empty="لا مبيعات أصناف فى الفترة."
            onOpen={(row) => onNavigate(targetOf(row.link))}
          />
        </Panel>
        <Panel title="أكبر بنود المصروفات" icon="expense">
          <RankedList
            rows={data.top.expenses}
            empty="لا مصروفات تشغيلية فى الفترة."
            onOpen={(row) => onNavigate(targetOf(row.link))}
          />
        </Panel>
      </div>

      <div className="dash-row">
        <Panel title="أرصدة الخزائن والبنوك" icon="bank">
          <ul className="dash-balances">
            {[...data.cash.cashboxes, ...data.cash.bankAccounts]
              .filter((item) => item.isActive || !Money.of(item.balance).isZero())
              .map((item) => {
                const isBank = data.cash.bankAccounts.includes(item);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() =>
                        onNavigate(
                          isBank
                            ? { kind: 'reconciliation', bankAccountId: item.id }
                            : { kind: 'treasuryAccounts' },
                        )
                      }
                    >
                      <span>
                        <i className={'dash-chip ' + (isBank ? 'bank' : 'box')}>
                          {isBank ? 'بنك' : 'خزينة'}
                        </i>
                        {item.nameAr}
                      </span>
                      <b>{formatMoney(item.balance)}</b>
                    </button>
                  </li>
                );
              })}
            {!Money.of(data.cash.unassigned).isZero() && (
              <li className="dash-legend-note">
                <span>على حسابات نقدية غير مربوطة بخزينة أو بنك</span>
                <b>{formatMoney(data.cash.unassigned)}</b>
              </li>
            )}
          </ul>
        </Panel>
        <Panel
          title="أعمار ديون الموردين"
          icon="truck"
          action={
            <button
              type="button"
              className="ghost"
              onClick={() => onNavigate({ kind: 'reports', tab: 'apAging' })}
            >
              التقرير
            </button>
          }
        >
          <AgingDonut
            aging={data.payablesAging}
            total={data.payables.total}
            currency={currency}
            subject="الموردين"
            unappliedLabel="سدادات لم تُخصص (تُخصم)"
            onOpen={() => onNavigate({ kind: 'reports', tab: 'apAging' })}
          />
        </Panel>
      </div>

      <div className="dash-status">
        <button
          type="button"
          className="card card-link"
          onClick={() => onNavigate({ kind: 'periods' })}
        >
          <span className="label">الفترة المالية</span>
          <span className="num">
            {data.currentPeriod
              ? data.currentPeriod.status === 'OPEN'
                ? 'مفتوحة'
                : 'مقفلة'
              : 'غير معرّفة'}
          </span>
          <span className="label">
            {data.currentPeriod
              ? data.currentPeriod.startDate + ' ← ' + data.currentPeriod.endDate
              : 'لا فترة تحتوى التاريخ'}
          </span>
        </button>
        {eInvoiceCard}
        <button
          type="button"
          className="card card-link"
          onClick={() => onNavigate({ kind: 'reportsCenter' })}
        >
          <span className="label">مركز التقارير</span>
          <span className="num">كل التقارير</span>
          <span className="label">المبيعات، المشتريات، النقدية، والسجلات</span>
        </button>
      </div>
    </div>
  );
}
